import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
	Popper,
	Paper,
	ClickAwayListener,
	styled,
	useTheme,
	PopperProps,
} from '@mui/material';

const Arrow = styled('div')({
	position: 'absolute',
	fontSize: '1em',
	width: '3em',
	height: '3em',
	'&::before': {
		content: '""',
		margin: 'auto',
		display: 'block',
		width: 0,
		height: 0,
		borderStyle: 'solid',
	},
});

const StyledPopper = styled(Popper, {
	shouldForwardProp: (prop) => prop !== 'arrow',
})(({ theme }) => ({
	zIndex: theme.zIndex.modal - 1,
	width: 'fit-content',
	'& > div': {
		position: 'relative',
	},
	'&[data-popper-placement*="bottom"]': {
		'& .MuiPopper-arrow': {
			top: 0,
			left: 0,
			marginTop: '-0.9em',
			width: '3em',
			height: '1em',
			'&::before': {
				borderWidth: '0 1em 1em 1em',
				borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
			},
		},
	},
	'&[data-popper-placement*="top"]': {
		'& .MuiPopper-arrow': {
			bottom: 0,
			left: 0,
			marginBottom: '-0.9em',
			width: '3em',
			height: '1em',
			'&::before': {
				borderWidth: '1em 1em 0 1em',
				borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
			},
		},
	},
	'&[data-popper-placement*="right"]': {
		'& .MuiPopper-arrow': {
			left: 0,
			marginLeft: '-0.9em',
			height: '3em',
			width: '1em',
			'&::before': {
				borderWidth: '1em 1em 1em 0',
				borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
			},
		},
	},
	'&[data-popper-placement*="left"]': {
		'& .MuiPopper-arrow': {
			right: 0,
			marginRight: '-0.9em',
			height: '3em',
			width: '1em',
			'&::before': {
				borderWidth: '1em 0 1em 1em',
				borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
			},
		},
	},
}));

export interface ArrowPopperProps {
	open: boolean;
	anchorEl: PopperProps['anchorEl'];
	placement?: PopperProps['placement'];
	onClickAway?: () => void;
	children: React.ReactNode;
	width?: string | number;
	zIndex?: number;
	disablePortal?: boolean;
	enableClickAway?: boolean;
	transformWrapperRef?: React.RefObject<any>;
	enableCanvasPan?: boolean;
	clickAwayLockedRef?: React.RefObject<boolean>;
}

const ArrowPopper: React.FC<ArrowPopperProps> = ({
	open,
	anchorEl,
	placement = 'right-start',
	onClickAway,
	children,
	width = '21.875rem',
	zIndex,
	disablePortal = false,
	enableClickAway = true,
	transformWrapperRef,
	enableCanvasPan = true,
	clickAwayLockedRef,
}) => {
	const theme = useTheme();
	const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);
	const [actualPlacement, setActualPlacement] = useState(placement);
	const popperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		return;

		if (!open || !anchorEl || !enableCanvasPan) return;

		const anchorElement =
			typeof anchorEl === 'function' ? anchorEl() : anchorEl;
		if (!anchorElement || !transformWrapperRef?.current) return;

		const timeoutId = setTimeout(() => {
			const transformInstance = transformWrapperRef.current;
			if (!transformInstance) return;

			const canvasElement = document.getElementById('main-canvas');
			if (!canvasElement) return;

			const canvasRect = canvasElement.getBoundingClientRect();
			const currentState = transformInstance.state;
			if (!currentState) return;
			const currentScale = currentState.scale || 1;
			const currentX = currentState.positionX || 0;
			const currentY = currentState.positionY || 0;

			const popperWidth = typeof width === 'string' ? parseInt(width) : width;
			const popperHeight = 450;
			const padding = 20;

			const blockRect = anchorElement.getBoundingClientRect();

			const placementBase = placement.split('-')[0];
			let totalRequiredWidth = blockRect.width;
			let totalRequiredHeight = blockRect.height;

			switch (placementBase) {
				case 'right':
					totalRequiredWidth = blockRect.width + popperWidth + padding;
					totalRequiredHeight = Math.max(blockRect.height, popperHeight);
					break;
				case 'left':
					totalRequiredWidth = blockRect.width + popperWidth + padding;
					totalRequiredHeight = Math.max(blockRect.height, popperHeight);
					break;
				case 'top':
					totalRequiredWidth = Math.max(blockRect.width, popperWidth);
					totalRequiredHeight = blockRect.height + popperHeight + padding;
					break;
				case 'bottom':
					totalRequiredWidth = Math.max(blockRect.width, popperWidth);
					totalRequiredHeight = blockRect.height + popperHeight + padding;
					break;
			}

			totalRequiredWidth += padding * 2;
			totalRequiredHeight += padding * 2;

			const availableWidth = canvasRect.width;
			const availableHeight = canvasRect.height;

			let targetScale = currentScale;
			let needsZoomOut = false;

			if (
				totalRequiredWidth > availableWidth ||
				totalRequiredHeight > availableHeight
			) {
				const scaleForWidth =
					totalRequiredWidth > availableWidth
						? ((availableWidth * 0.85) / totalRequiredWidth) * currentScale
						: currentScale;
				const scaleForHeight =
					totalRequiredHeight > availableHeight
						? ((availableHeight * 0.85) / totalRequiredHeight) * currentScale
						: currentScale;

				targetScale = Math.min(scaleForWidth, scaleForHeight);

				if (targetScale < currentScale) {
					targetScale = Math.max(targetScale, 0.3333);
					needsZoomOut = true;
				}
			}

			let targetX = currentX;
			let targetY = currentY;
			let needsPan = false;

			const blockInCanvasLeft = blockRect.left - canvasRect.left;
			const blockInCanvasTop = blockRect.top - canvasRect.top;
			const blockInCanvasRight = blockRect.right - canvasRect.left;
			const blockInCanvasBottom = blockRect.bottom - canvasRect.top;

			const extraPadding = padding * 2;

			switch (placementBase) {
				case 'right':
					const rightOverflow =
						blockInCanvasRight +
						popperWidth +
						padding +
						extraPadding -
						canvasRect.width;
					if (rightOverflow > 0) {
						targetX =
							currentX -
							rightOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					const verticalCenterRight = blockInCanvasTop + blockRect.height / 2;
					const popperTopRight = verticalCenterRight - popperHeight / 2;
					const popperBottomRight = verticalCenterRight + popperHeight / 2;
					if (popperTopRight < padding) {
						const topOverflow = Math.abs(popperTopRight - padding);
						targetY =
							currentY +
							topOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					} else if (popperBottomRight > canvasRect.height - padding) {
						const bottomOverflow =
							popperBottomRight - (canvasRect.height - padding);
						targetY =
							currentY -
							bottomOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					break;
				case 'left':
					const leftOverflow = Math.abs(
						blockInCanvasLeft - popperWidth - padding - extraPadding
					);
					if (blockInCanvasLeft - popperWidth - padding - extraPadding < 0) {
						targetX =
							currentX +
							leftOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					const verticalCenterLeft = blockInCanvasTop + blockRect.height / 2;
					const popperTopLeft = verticalCenterLeft - popperHeight / 2;
					const popperBottomLeft = verticalCenterLeft + popperHeight / 2;
					if (popperTopLeft < padding) {
						const topOverflow = Math.abs(popperTopLeft - padding);
						targetY =
							currentY +
							topOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					} else if (popperBottomLeft > canvasRect.height - padding) {
						const bottomOverflow =
							popperBottomLeft - (canvasRect.height - padding);
						targetY =
							currentY -
							bottomOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					break;
				case 'top':
					const topOverflow = Math.abs(
						blockInCanvasTop - popperHeight - padding - extraPadding
					);
					if (blockInCanvasTop - popperHeight - padding - extraPadding < 0) {
						targetY =
							currentY +
							topOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					const horizontalCenterTop = blockInCanvasLeft + blockRect.width / 2;
					const popperLeftTop = horizontalCenterTop - popperWidth / 2;
					const popperRightTop = horizontalCenterTop + popperWidth / 2;
					if (popperLeftTop < padding) {
						const leftOverflow = Math.abs(popperLeftTop - padding);
						targetX =
							currentX +
							leftOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					} else if (popperRightTop > canvasRect.width - padding) {
						const rightOverflow = popperRightTop - (canvasRect.width - padding);
						targetX =
							currentX -
							rightOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					break;
				case 'bottom':
					const bottomOverflow =
						blockInCanvasBottom +
						popperHeight +
						padding +
						extraPadding -
						canvasRect.height;
					if (bottomOverflow > 0) {
						targetY =
							currentY -
							bottomOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					const horizontalCenterBottom =
						blockInCanvasLeft + blockRect.width / 2;
					const popperLeftBottom = horizontalCenterBottom - popperWidth / 2;
					const popperRightBottom = horizontalCenterBottom + popperWidth / 2;
					if (popperLeftBottom < padding) {
						const leftOverflow = Math.abs(popperLeftBottom - padding);
						targetX =
							currentX +
							leftOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					} else if (popperRightBottom > canvasRect.width - padding) {
						const rightOverflow =
							popperRightBottom - (canvasRect.width - padding);
						targetX =
							currentX -
							rightOverflow * (needsZoomOut ? targetScale / currentScale : 1);
						needsPan = true;
					}
					break;
			}

			if (needsZoomOut || needsPan) {
				if (transformInstance.setTransform) {
					transformInstance.setTransform(targetX, targetY, targetScale, 300);
				}
			}
		}, 100);

		return () => clearTimeout(timeoutId);
	}, [open, anchorEl, placement, width, enableCanvasPan, transformWrapperRef]);

	const handleClickAway = useCallback(
		(event: Event | React.SyntheticEvent) => {
			if (clickAwayLockedRef?.current) {
				return;
			}

			const target = event.target as Element;
			if (
				target?.closest(
					'[role="listbox"], [role="option"], .MuiPopover-root, .MuiMenu-root, .MuiMenuItem-root, .MuiBackdrop-root, .MuiDialog-root, .MuiDialogContent-root'
				)
			) {
				return;
			}

			if (popperRef.current && popperRef.current.contains(target)) {
				return;
			}

			onClickAway?.();
		},
		[onClickAway]
	);

	const popperContent = (
		<StyledPopper
			open={open}
			anchorEl={anchorEl}
			placement={placement}
			disablePortal={disablePortal}
			modifiers={[
				{
					name: 'offset',
					options: {
						offset: [0, 8],
					},
				},
				{
					name: 'flip',
					enabled: false,
				},
				{
					name: 'preventOverflow',
					enabled: false,
				},
				{
					name: 'arrow',
					enabled: true,
					options: {
						element: arrowRef,
						padding: 8,
					},
				},
				{
					name: 'onUpdate',
					enabled: true,
					phase: 'afterWrite' as any,
					fn: ({ state }) => {
						if (state.placement !== actualPlacement) {
							setActualPlacement(state.placement);
						}
					},
				},
				{
					name: 'computeStyles',
					options: {
						adaptive: true,
						roundOffsets: false,
					},
				},
			]}
			sx={{
				zIndex: zIndex ?? theme.zIndex.modal - 1,
				width,
			}}
		>
			<div ref={popperRef}>
				<Arrow ref={setArrowRef} className="MuiPopper-arrow" />
				<Paper
					sx={{
						maxWidth: width,
						position: 'relative',
						'& .MuiDialogContent-root': {
							p: theme.spacing(2),
						},
						'& .MuiDialogActions-root': {
							borderTop: '1px solid',
							borderColor: theme.palette.divider,
							p: theme.spacing(1, 2),
						},
					}}
				>
					{children}
				</Paper>
			</div>
		</StyledPopper>
	);

	if (enableClickAway && onClickAway) {
		return (
			<ClickAwayListener onClickAway={handleClickAway}>
				{popperContent}
			</ClickAwayListener>
		);
	}

	return popperContent;
};

export default ArrowPopper;
